import axios, { AxiosRequestHeaders } from 'axios'
import APIService from '../APIService/APIServiceImpl'
import BookingServiceV1 from './BookingServiceV1'
import { BookingQuoteDTO, InsuranceQuote, PaymentPurposeEnum, Promocode, QuoteResponseDTO, ServiceQuote, TravellerQuote, UserInfo } from "../../../../../requests-module"
import getCookie from '@/utils/getCookie'
import handleIresaError from "../../func/handleIresaError"
import { CSEService } from '@/service'
import { CSECompanyData } from '@/classes/CSE'

// User
import loggedInUser from '@/state/loggedInUser'

// just for readability
const isRedirectToErrorTrue = true
const isRedirectToErrorFalse = false
const isThrowErrorTrue = true
const isThrowErrorFalse = false

export default class BookingServiceV1Impl
    extends APIService
    implements BookingServiceV1 {
    constructor() {
        const serviceUrl = BookingServiceV1Impl.getBaseUrl()
        super(serviceUrl)
    }

    private static getBaseUrl(): string {
        const serviceUrl: string | undefined = window.env.VUE_APP_BOOKING_FUNNEL_BASE_URL || 'https://api.travelski.com/booking-funnel'
        if (!serviceUrl) throw new Error("VUE_APP_BOOKING_FUNNEL_BASE_URL cannot be undefined")
        return serviceUrl
    }

    async postBookingQuote(
        dto: BookingQuoteDTO, detail = false
    ): Promise<QuoteResponseDTO | undefined> {
        const params = {
            ...this.getDefaultParams(),
            detail,
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/quote`
        try {
            const { data } = await axios.post(url, JSON.stringify(dto), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorTrue, isThrowErrorTrue)
        }
    }

    async getBookingTokenQuote(
        token: string, detail = false
    ): Promise<QuoteResponseDTO | undefined> {
        if (!token) throw new Error("cannot make '/booking/${token}/quote' request without token")
        const params = {
            ...this.getDefaultParams(),
            detail,
        }

        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/quote`
        try {
            const { data } = await axios.get(url, {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorTrue, isThrowErrorTrue)
        }
    }


    async updateParticipants(
        requestData: { token: string, travellers: TravellerQuote[] }
    ): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make PUT '/booking/${token}/participants' request without token")
        const body = requestData.travellers
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/participants` // TODO use this later
        try {
            const { data } = await axios.put(url, JSON.stringify(body), {
                ...this.getDefaultParams(),
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorFalse, isThrowErrorFalse)
        }
    }

    async updateServices(requestData: { token: string, services: { [familyId: string]: ServiceQuote[] } }): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make PUT '/booking/${token}/services' request without token")
        const body = requestData.services
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/services`
        try {
            const { data } = await axios.put(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorFalse, isThrowErrorFalse)
        }

    }

    async updateInsurances(requestData: { token: string, insurances: { [familyId: string]: InsuranceQuote[] } }): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make PUT '/booking/${token}/insurances' request without token")
        const body = requestData.insurances
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/insurances`
        try {
            const { data } = await axios.put(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorFalse, isThrowErrorFalse)
        }

    }

    async setUserInfo(requestData: { token: string, userInfo: UserInfo }): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make POST '/booking/${token}/set-user-info' request without token")
        const body = requestData.userInfo
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/set-user-info`
        try {
            const { data } = await axios.post(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorTrue, isThrowErrorTrue)
        }
    }

    async applyPromocode(
        requestData: {
            token: string,
            promocodeDTO: Promocode,
        }): Promise<Promocode | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make POST '/booking/${token}/promocode' request without token")
        const body = requestData.promocodeDTO
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/promocode`
        try {
            const { data } = await axios.post(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorFalse, isThrowErrorFalse)
        }
    }


    async getBooking(requestData: { token: string }): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make GET '/booking/${token}/get-booking' request without token")
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/get-booking`
        try {
            const { data } = await axios.get(url, {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, true, isThrowErrorFalse)
        }
    }

    async getBookingByContractAndUser(
        contract_id: string, user_id: string, detail = false
    ): Promise<QuoteResponseDTO | undefined> {
        const params = {
            ...this.getDefaultParams(),
            contract_id,
            user_id,
            detail,
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/get-booking`
        try {
            const { data } = await axios.post(url, {}, {
                params,
                headers,
            })
            return data
        } catch (err) {
            console.error("err getBookingByContractAndUser: ", err)
            await handleIresaError(err, isRedirectToErrorTrue, isThrowErrorTrue)
        }
    }

    async createQuote(requestData: { token: string }): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make POST '/booking/${token}/create-quote' request without token")
        const body = {}
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }

        console.log("CREATE QUOTE PARAMS: ", params)
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/create-quote`
        try {
            const { data } = await axios.post(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorTrue, isThrowErrorTrue)
        }
    }

    async createBooking(requestData: { token: string }): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make POST '/booking/${token}/create-booking' request without token")
        const body = {}
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/create-booking`
        try {
            const { data } = await axios.post(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorTrue, isThrowErrorTrue)
        }

    }

    async cancelBooking(requestData: { token: string }): Promise<QuoteResponseDTO | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make PUT '/booking/${token}/cancel-booking' request without token")
        const body = {}
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/cancel-booking`
        try {
            const { data } = await axios.put(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorFalse, isThrowErrorFalse)
        }

    }

    async createContact(requestData: { token: string, userInfo: UserInfo }): Promise<string | undefined> {
        const token = requestData.token
        if (!token) throw new Error("cannot make POST '/booking/${token}/create-contact' request without token")
        const body = requestData.userInfo
        const params = {
            ...this.getDefaultParams(),
            detail: true
        }
        const headers = {
            ...this.getDefaultHeaders(),
            "Authorization": getCookie("user-id-token") || ""
        }
        const url = `${this.getServiceUrl()}/${token}/create-contact`
        try {
            const { data } = await axios.post(url, JSON.stringify(body), {
                params,
                headers,
            })
            return data
        } catch (err) {
            await handleIresaError(err, isRedirectToErrorTrue, isThrowErrorTrue)
        }

    }

    public override getDefaultHeaders(): AxiosRequestHeaders {
        const locale = this.getLocale()
        return {
            'Accept-Language': locale,
            'Content-Type': 'application/json',
            // "locale": locale,
        }
    }

    public getDefaultParams(): {
        [key: string]: string;
    } {
        const locale = this.getLocale()
        const params: { [key: string]: string } = { locale, purpose: PaymentPurposeEnum.FIRST_PAYMENT }
        const isCSE = locale.includes("CSE")
        if (window.company && isCSE) {
            const isCGOS = window.company.toUpperCase().includes("CGOS")
            params['company'] = window.company
            const cseDataRaw = getCookie("companyData")
            if (!cseDataRaw && isCGOS) throw new Error("")
            const cseService: CSEService = new CSEService(locale)
            if (cseService.isCSE) cseService.init()
            const companyData: CSECompanyData | undefined = cseService.companyData
            if (companyData?.numeroUsager) params['numeroUsager'] = companyData.numeroUsager
        }
        return params
    }

}